import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react'
import { audioPlayerObjCache, detectSSLErrorForUrl, getMediaErrorMessage } from './helper'
import { AUDIO_LOADING_STATE, AUDIO_READY_STATES, SSL_CERT_ERROR_MESSAGE } from './constant'

type HandleBringBackCardFn = ((index: number) => void) | null
interface AudioContextType {
  toggle: () => void
  seek: (time: number) => void
  changeVolume: (v: number) => void
  changeSpeed: (s: number) => void
  load: (src: string, id: string, title: string, opts?: { autoplay?: boolean }) => void
  setIsInlineVisible: (v: boolean) => void
  close: () => void
  setFallbackMessage: (str: string) => void
  setVirtualCardIndex: (index: number) => void
  handleBringBackCard: HandleBringBackCardFn
  setHandleBringBackCard: (fn: HandleBringBackCardFn) => void
  virtualCardIndex: number | null
  currentTime: number
  duration: number
  buffered: number
  isPlaying: boolean
  isClosed: boolean
  isInlineVisible: boolean
  activeId?: string
  title: string
  fallbackMessage: string | null
  errorCode: null | number
}

const AudioContext = createContext<AudioContextType | null>(null)

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const prevPlayerRef = useRef<HTMLAudioElement | null>(null)
  const listenersRef = useRef<{
    player: HTMLAudioElement | null
    onLoadedMetadata?: () => void
    onTimeUpdate?: () => void
    onEnded?: () => void
    onProgress?: () => void
    onError?: () => void
  } | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [activeId, setActiveId] = useState<string | undefined>(undefined)
  const [isInlineVisible, setIsInlineVisible] = useState(true)
  const [isClosed, setIsClosed] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [virtualCardIndex, setVirtualCardIndex] = useState<number | null>(null)
  const [handleBringBackCard, setHandleBringBackCard] = useState<HandleBringBackCardFn>(null)
  const [duration, setDuration] = useState(0)
  const [buffered, setBuffered] = useState(0)
  const [title, setActiveTitle] = useState('')
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<number | null>(null)

  useEffect(
    () => () => {
      const player = audioRef.current
      if (player) {
        try {
          player.pause()
        } catch {}
      }
      if (listenersRef.current?.player) {
        const { player, onLoadedMetadata, onTimeUpdate, onEnded, onProgress, onError } = listenersRef.current
        player.removeEventListener('loadedmetadata', onLoadedMetadata!)
        player.removeEventListener('timeupdate', onTimeUpdate!)
        player.removeEventListener('ended', onEnded!)
        player.removeEventListener('progress', onProgress!)
        player.removeEventListener('error', onError!)
        listenersRef.current = null
      }
    },
    [],
  )

  const handleError = useCallback((player: HTMLAudioElement) => {
    const error = player.error
    if (!error) return

    const defaultMessage = getMediaErrorMessage(error.code, error.message)
    setErrorCode(error.code)
    setIsPlaying(false)
    setIsClosed(true)

    const url = player.currentSrc || (typeof player.src === 'string' ? player.src : '')
    const willProbeSSL =
      url.startsWith('https://') &&
      (error.code === MediaError.MEDIA_ERR_NETWORK || error.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED)

    if (willProbeSSL) {
      setFallbackMessage(AUDIO_LOADING_STATE)
      detectSSLErrorForUrl(url)
        .then((isSSL) => setFallbackMessage(isSSL ? SSL_CERT_ERROR_MESSAGE : defaultMessage))
        .catch(() => setFallbackMessage(defaultMessage))
    } else {
      setFallbackMessage(defaultMessage)
    }
  }, [])

  const attachListeners = (player: HTMLAudioElement) => {
    if (listenersRef.current?.player && listenersRef.current.player !== player) {
      const prev = listenersRef.current.player
      const { onLoadedMetadata, onTimeUpdate, onEnded, onProgress, onError } = listenersRef.current
      prev.removeEventListener('loadedmetadata', onLoadedMetadata!)
      prev.removeEventListener('timeupdate', onTimeUpdate!)
      prev.removeEventListener('ended', onEnded!)
      prev.removeEventListener('progress', onProgress!)
      prev.removeEventListener('error', onError!)
      listenersRef.current = null
    }

    if (listenersRef.current && listenersRef.current.player === player) return

    const onLoadedMetadata = () => {
      setDuration(player.duration || 0)
      setFallbackMessage(null)
      setErrorCode(null)
    }

    const onTimeUpdate = () => {
      setCurrentTime(Math.round(player.currentTime))
    }
    const onEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }
    const onProgress = () => {
      try {
        if (player.buffered && player.buffered.length > 0) {
          setBuffered(player.buffered.end(player.buffered.length - 1))
        } else {
          setBuffered(0)
        }
      } catch (e) {
        setBuffered(0)
      }
    }

    const onError = () => handleError(player)

    player.addEventListener('loadedmetadata', onLoadedMetadata)
    player.addEventListener('timeupdate', onTimeUpdate)
    player.addEventListener('ended', onEnded)
    player.addEventListener('progress', onProgress)
    player.addEventListener('error', onError)

    listenersRef.current = { player, onLoadedMetadata, onTimeUpdate, onEnded, onProgress, onError }
    prevPlayerRef.current = player
  }

  const load = useCallback(
    (src: string, id: string, title: string, opts?: { autoplay?: boolean }) => {
      if (!src) {
        return
      }

      let player = audioPlayerObjCache.get(src)

      if (!player) {
        player = new Audio(src)
        player.preload = 'metadata'
        audioPlayerObjCache.set(src, player)
      }

      if (audioRef.current && audioRef.current !== player) {
        try {
          audioRef.current.pause()
        } catch (e) {}
      }

      audioRef.current = player
      setActiveId(id)
      setActiveTitle(title)
      setErrorCode(null)

      attachListeners(player)

      if (player.readyState >= AUDIO_READY_STATES.HAVE_METADATA) {
        setDuration(player.duration || 0)
        setFallbackMessage(null)
      } else {
        setFallbackMessage(AUDIO_LOADING_STATE)
      }

      if (opts?.autoplay) {
        player
          .play()
          .then(() => {
            setIsPlaying(true)
            setIsClosed(false)
          })
          .catch(() => {
            handleError(player)
          })
      } else {
        setIsPlaying(!player.paused)
      }
    },
    [handleError],
  )

  const close = () => {
    const player = audioRef.current
    if (player) {
      player.pause()
    }
    setIsClosed(true)
    setIsPlaying(false)
    setErrorCode(null)
    setFallbackMessage(null)
  }

  const toggle = useCallback(() => {
    const player = audioRef.current
    if (!player) return
    if (player.paused) {
      player
        .play()
        .then(() => {
          setIsPlaying(true)
          setIsClosed(false)
        })
        .catch(() => handleError(player))
    } else {
      player.pause()
      setIsPlaying(false)
    }
  }, [handleError])

  const seek = useCallback((time: number) => {
    const player = audioRef.current
    if (!player) return
    const performSeek = () => {
      const dur = player.duration || 0
      const safeTime = Math.max(0, Math.min(dur, time))
      player.currentTime = safeTime
      setCurrentTime(safeTime)
    }
    if (player.readyState >= AUDIO_READY_STATES.HAVE_METADATA) {
      performSeek()
    } else {
      const metadataHandler = () => {
        performSeek()
        player.removeEventListener('loadedmetadata', metadataHandler)
      }
      player.addEventListener('loadedmetadata', metadataHandler)
    }
  }, [])

  const changeVolume = useCallback((v: number) => {
    const player = audioRef.current
    if (!player) return
    const normalized = Math.max(0, Math.min(1, v / 100))
    player.volume = normalized
  }, [])

  const changeSpeed = useCallback((s: number) => {
    const player = audioRef.current
    if (!player) return
    player.playbackRate = s
  }, [])

  return (
    <AudioContext.Provider
      value={{
        toggle,
        seek,
        changeVolume,
        changeSpeed,
        load,
        setIsInlineVisible,
        close,
        setFallbackMessage,
        setVirtualCardIndex,
        handleBringBackCard,
        setHandleBringBackCard,
        virtualCardIndex,
        isClosed,
        isPlaying,
        title,
        currentTime,
        duration,
        buffered,
        isInlineVisible,
        activeId,
        fallbackMessage,
        errorCode,
      }}
    >
      {children}
    </AudioContext.Provider>
  )
}

export const useAudio = () => {
  const ctx = useContext(AudioContext)
  if (!ctx) throw new Error('useAudio must be used within AudioProvider')
  return ctx
}
